/* @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx, useTheme } from '@emotion/react';
import type { Interpolation } from '@emotion/react';

import * as React from 'react';
import { useLayoutEffect, useState } from 'react';
import type { MouseEventHandler } from 'react';

import { copyTest } from '@coursera-web/copy-test';

import { Button, Grid, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import Markdown from 'bundles/cms/components/Markdown';
import type { Maybe } from 'bundles/xddp/__generated__/baseContentfulTypes';
import Container from 'bundles/xddp/components/shared/Container';
import { PaymentOptionsLink } from 'bundles/xddp/components/shared/PaymentOptionsLink';
import type { AtAGlanceProps } from 'bundles/xddp/types';

import _t from 'i18n!nls/xddp';

type Props = AtAGlanceProps & {
  slug: string;
};

type AtAGlanceItemProps = {
  label?: string | null;
  content?: React.ReactNode;
};

type AtAGlanceItemPropsTextOnly = {
  title?: Maybe<string>;
  description?: Maybe<string>;
};

type ExpandableSectionProps = {
  children: Maybe<React.ReactNode[]>;
  isExpanded: boolean;
  toggleExpanded: MouseEventHandler<HTMLButtonElement>;
  displayButton: boolean;
};

const styles: Record<string, Interpolation<Theme>> = {
  atAGlanceBannerWrapper: () => css`
    border: 1px solid var(--cds-color-grey-50);
    border-radius: 8px;
    padding: var(--cds-spacing-300) 0;
    margin-top: 20px;
    margin-bottom: var(--cds-spacing-150);
    background: var(--cds-color-neutral-background-primary);
    z-index: 2;
    position: relative;
    box-shadow: 0 0 4px rgb(0 0 0 / 25%);

    div {
      ${breakpoints.between(600, 1023)} {
        :nth-of-type(1) {
          order: 1;
        }

        :nth-of-type(2) {
          order: 2;
        }

        :nth-of-type(3) {
          order: 4;
        }

        :nth-of-type(4) {
          order: 3;
        }

        :nth-of-type(5) {
          order: 5;
        }

        :nth-of-type(6) {
          order: 7;
          max-width: 50%;
        }

        :nth-of-type(7) {
          order: 6;
        }
      }
    }

    ${breakpoints.down('sm')} {
      border: none;
      margin-top: 25px;
      margin-bottom: var(--cds-spacing-400);
      box-shadow: none;
    }

    ${breakpoints.down('xs')} {
      margin: 0;
      border: 1px solid var(--cds-color-grey-50);
      border-radius: 0;
      margin-top: 20px;
      padding: var(--cds-spacing-300) 0 var(--cds-spacing-150);

      button {
        width: fit-content;
      }
    }
  `,

  gridParent: () => css`
    display: flex;
    flex-wrap: nowrap;

    ${breakpoints.down('sm')} {
      flex-wrap: wrap;
    }

    ${breakpoints.down('xs')} {
      flex-direction: column;
    }
  `,

  descriptionCard: () => css`
    padding: 0 var(--cds-spacing-150) 0 var(--cds-spacing-300);
    border-right: 1px solid var(--cds-color-grey-50);

    ${breakpoints.up('md')} {
      width: 20%;
    }

    ${breakpoints.between(600, 1023)} {
      width: 49.5%;
      flex-basis: initial;
    }

    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-150) var(--cds-spacing-200) var(--cds-spacing-150) var(--cds-spacing-400);
      border-right: none;
    }

    &:last-child {
      border-right: none;
    }

    p {
      margin-top: 5px;
      width: 174px;

      .rc-Markdown.styled p {
        line-height: 20px;
        margin-bottom: 0;
        font-family: 'Source Sans Pro', Arial, sans-serif;
      }

      .rc-Markdown.styled a {
        color: var(--cds-color-neutral-primary);
      }

      ${breakpoints.down('sm')} {
        width: fit-content;
        margin-top: 3px;
      }
    }
  `,

  middleBorderForTabScreenOne: () => css`
    border-left: 1px solid var(--cds-color-grey-50);
    margin-bottom: calc(var(--cds-spacing-400) * -1);
    margin-top: var(--cds-spacing-400);
    visibility: hidden;
    ${breakpoints.between(600, 1023)} {
      visibility: visible;
    }
  `,

  middleBorderForTabScreenTwo: () => css`
    border-left: 1px solid var(--cds-color-grey-50);
    margin-bottom: -44px;
    margin-top: var(--cds-spacing-400);
    visibility: hidden;
    ${breakpoints.between(600, 1023)} {
      visibility: visible;
    }
  `,

  linkStyle: () => css`
    color: var(--cds-color-neutral-primary);
    text-decoration: underline;
  `,

  buttonContainer: () => css`
    padding: var(--cds-spacing-150) var(--cds-spacing-200) var(--cds-spacing-150) var(--cds-spacing-400);
    display: block;
    text-align: right;
  `,
};

const AtAGlanceItem = ({ label, content }: AtAGlanceItemProps) => {
  return (
    <Grid css={styles.descriptionCard} item xs>
      {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
      <Typography variant="h2semibold">{label}</Typography>
      {content}
    </Grid>
  );
};

const AtAGlanceItemTextOnly = ({ title, description }: AtAGlanceItemPropsTextOnly) => {
  return (
    <AtAGlanceItem
      label={title}
      content={
        <Typography2 component="p" variant="bodySecondary">
          <Markdown source={description} />
        </Typography2>
      }
    />
  );
};

const NUM_ELEMENTS_ALWAYS_DISPLAYED = 3;

const ExpandableSection = ({ children, isExpanded, toggleExpanded, displayButton }: ExpandableSectionProps) => {
  return (
    <Grid container css={styles.gridParent} id="mobile-layout-gridparent">
      {children && children.slice(0, isExpanded ? children.length + 1 : NUM_ELEMENTS_ALWAYS_DISPLAYED)}
      {displayButton && (
        <Grid item xs css={styles.buttonContainer} data-testid="button-container">
          <Button variant="ghost" size="medium" onClick={toggleExpanded} data-testid="expand-button">
            {isExpanded ? _t('Show less') : _t('Show more')}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

const AtAGlanceSection = (props: Props) => {
  const {
    bannerAtAGlanceTitle1,
    bannerAtAGlanceDescription1,
    bannerAtAGlanceTitle2,
    bannerAtAGlanceDescription2,
    bannerAtAGlanceTitle3,
    bannerAtAGlanceDescription3,
    bannerAtAGlanceTitle4,
    bannerAtAGlanceDescription4,
    bannerAtAGlanceTitle5,
    bannerAtAGlanceDescription5,
    slug,
  } = props;

  const createAlias = (fieldName: string): `contentful-${string}` => `contentful-xddp-${fieldName}-${slug}-en`;
  const copy = {
    bannerAtAGlanceTitle1: copyTest(createAlias('bannerAtAGlanceTitle1'), bannerAtAGlanceTitle1),
    bannerAtAGlanceDescription1: copyTest(createAlias('bannerAtAGlanceDescription1'), bannerAtAGlanceDescription1),
    bannerAtAGlanceTitle2: copyTest(createAlias('bannerAtAGlanceTitle2'), bannerAtAGlanceTitle2),
    bannerAtAGlanceDescription2: copyTest(createAlias('bannerAtAGlanceDescription2'), bannerAtAGlanceDescription2),
    bannerAtAGlanceTitle3: copyTest(createAlias('bannerAtAGlanceTitle3'), bannerAtAGlanceTitle3),
    bannerAtAGlanceDescription3: copyTest(createAlias('bannerAtAGlanceDescription3'), bannerAtAGlanceDescription3),
    bannerAtAGlanceTitle4: copyTest(createAlias('bannerAtAGlanceTitle4'), bannerAtAGlanceTitle4),
    bannerAtAGlanceDescription4: copyTest(createAlias('bannerAtAGlanceDescription4'), bannerAtAGlanceDescription4),
    bannerAtAGlanceTitle5: copyTest(createAlias('bannerAtAGlanceTitle5'), bannerAtAGlanceTitle5),
    bannerAtAGlanceDescription5: copyTest(createAlias('bannerAtAGlanceDescription5'), bannerAtAGlanceDescription5),
  };

  const mobileWidthBreakpoint = useTheme().breakpoints.values.sm;
  const [isExpanded, setIsExpanded] = useState(true);
  const [displayExpandButton, setDisplayExpandButton] = useState(false);

  const toggleMobileExpanded = () => {
    setIsExpanded((current) => !current);
  };

  useLayoutEffect(() => {
    const handleResize = () => {
      const w = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
      if (w >= mobileWidthBreakpoint) {
        setIsExpanded(true);
        setDisplayExpandButton(false);
      } else {
        setDisplayExpandButton(true);
      }
    };

    // only do on initial load - fix flash of unexpanded on desktop layout
    const w = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
    if (w < mobileWidthBreakpoint) setIsExpanded(false);

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);

    // Passing in empty array so this only runs once, on mount/unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container className="atAGlanceSection-container">
      <div data-test="atAGlanceSection" css={styles.atAGlanceBannerWrapper}>
        <ExpandableSection
          isExpanded={isExpanded}
          toggleExpanded={toggleMobileExpanded}
          displayButton={displayExpandButton}
        >
          <AtAGlanceItemTextOnly title={copy.bannerAtAGlanceTitle1} description={copy.bannerAtAGlanceDescription1} />

          <div css={styles.middleBorderForTabScreenOne} />

          <AtAGlanceItemTextOnly title={copy.bannerAtAGlanceTitle2} description={copy.bannerAtAGlanceDescription2} />

          <AtAGlanceItemTextOnly title={copy.bannerAtAGlanceTitle3} description={copy.bannerAtAGlanceDescription3} />

          <div css={styles.middleBorderForTabScreenTwo} />

          <AtAGlanceItemTextOnly title={copy.bannerAtAGlanceTitle4} description={copy.bannerAtAGlanceDescription4} />

          <AtAGlanceItem
            label={copy.bannerAtAGlanceTitle5}
            content={
              <Typography2 component="p" variant="bodySecondary">
                <PaymentOptionsLink tuitionLinkText={copy.bannerAtAGlanceDescription5} />
              </Typography2>
            }
          />
        </ExpandableSection>
      </div>
    </Container>
  );
};

export { ExpandableSection };
export default AtAGlanceSection;
